export const theme = {
  colors: {
    // App colors (keep existing for dashboard)
    primary: {
      DEFAULT: "#1A1F2C",
      hover: "#252A3A",
      light: "#9b87f5",
      mid: "#7E69AB",
      dark: "#6E59A5",
      subtle: "#D6BCFA",
    },
    text: {
      heading: "#111827",
      body: "#4b5563",
      muted: "#6b7280",
    },
    border: "rgba(155, 135, 245, 0.10)",
    glass: {
      bg: "rgba(255, 255, 255, 0.10)",
      border: "rgba(255, 255, 255, 0.20)",
    },
    status: {
      greenBg: "#dcfce7",
      greenText: "#166534",
      yellowBg: "#fef9c3",
      yellowText: "#854d0e",
      blueBg: "#dbeafe",
      blueText: "#1e40af",
      redBg: "#fee2e2",
      redText: "#991b1b",
      grayBg: "#f3f4f6",
      grayText: "#1f2937",
    },
    // Aura-inspired landing colors (Light Mode)
    light: {
      background: "hsl(0, 0%, 99%)",
      foreground: "hsl(240, 20%, 10%)",
      primary: "hsl(258, 90%, 58%)",
      accent: "hsl(16, 90%, 58%)",
      muted: {
        DEFAULT: "hsl(240, 10%, 96%)",
        foreground: "hsl(240, 10%, 45%)",
      },
      border: "hsl(240, 10%, 90%)",
      card: {
        background: "hsl(0, 0%, 100%)",
        foreground: "hsl(240, 20%, 10%)",
      },
    },
    // Aura-inspired landing colors (Dark Mode)
    dark: {
      background: "hsl(240, 20%, 6%)",
      foreground: "hsl(0, 0%, 98%)",
      primary: "hsl(258, 85%, 65%)",
      accent: "hsl(16, 85%, 60%)",
      muted: {
        DEFAULT: "hsl(240, 15%, 15%)",
        foreground: "hsl(240, 10%, 60%)",
      },
      border: "hsl(240, 15%, 18%)",
      card: {
        background: "hsl(240, 15%, 10%)",
        foreground: "hsl(0, 0%, 98%)",
      },
    },
    // Gradients
    gradients: {
      primary: "linear-gradient(135deg, hsl(258, 90%, 58%) 0%, hsl(280, 85%, 55%) 50%, hsl(320, 80%, 55%) 100%)",
      secondary: "linear-gradient(135deg, hsl(16, 90%, 58%) 0%, hsl(35, 95%, 55%) 100%)",
      hero: "linear-gradient(135deg, hsl(258, 90%, 65%) 0%, hsl(280, 85%, 60%) 30%, hsl(320, 80%, 60%) 60%, hsl(16, 90%, 65%) 100%)",
      card: "linear-gradient(145deg, hsl(0, 0%, 100%) 0%, hsl(258, 30%, 99%) 100%)",
      cardDark: "linear-gradient(145deg, hsl(240, 15%, 12%) 0%, hsl(258, 20%, 10%) 100%)",
      // Feature icon gradients
      yellowOrange: "linear-gradient(to bottom right, hsl(48, 96%, 53%), hsl(25, 95%, 53%))",
      greenEmerald: "linear-gradient(to bottom right, hsl(142, 76%, 36%), hsl(160, 84%, 39%))",
      blueCyan: "linear-gradient(to bottom right, hsl(217, 91%, 60%), hsl(199, 89%, 48%))",
      purplePink: "linear-gradient(to bottom right, hsl(262, 83%, 58%), hsl(330, 81%, 60%))",
      pinkRose: "linear-gradient(to bottom right, hsl(330, 81%, 60%), hsl(350, 89%, 60%))",
      indigoViolet: "linear-gradient(to bottom right, hsl(239, 84%, 67%), hsl(258, 90%, 66%))",
    },
    // Glass effects
    glass: {
      light: {
        background: "hsla(0, 0%, 100%, 0.8)",
        border: "hsla(240, 10%, 90%, 0.5)",
      },
      dark: {
        background: "hsla(240, 15%, 10%, 0.8)",
        border: "hsla(240, 15%, 25%, 0.5)",
      },
    },
    // Legacy landing colors (for backward compatibility)
    landing: {
      background: {
        primary: "hsl(240, 20%, 6%)",
        secondary: "hsl(240, 20%, 8%)",
        tertiary: "hsl(240, 15%, 10%)",
        gradient: "linear-gradient(180deg, hsl(240, 20%, 6%) 0%, hsl(240, 20%, 8%) 100%)",
      },
      text: {
        primary: "hsl(0, 0%, 98%)",
        secondary: "hsl(240, 10%, 60%)",
        tertiary: "hsl(240, 10%, 45%)",
        accent: "hsl(258, 85%, 65%)",
      },
      glass: {
        background: "hsla(240, 15%, 10%, 0.8)",
        border: "hsla(240, 15%, 25%, 0.5)",
        hover: "hsla(240, 15%, 12%, 0.9)",
      },
      accent: {
        primary: "hsl(258, 85%, 65%)",
        hover: "hsl(258, 85%, 70%)",
        glow: "hsla(258, 85%, 65%, 0.5)",
      },
    },
  },
  typography: {
    fontFamily: {
      display: '"Plus Jakarta Sans", system-ui, sans-serif',
      body: '"Inter", system-ui, sans-serif',
    },
    fontSize: {
      hero: "clamp(2.5rem, 8vw, 4.5rem)", // 40px to 72px
      h1: "clamp(2rem, 5vw, 3.75rem)", // 32px to 60px
      h2: "clamp(1.875rem, 4vw, 3rem)", // 30px to 48px
      h3: "clamp(1.5rem, 3vw, 2rem)", // 24px to 32px
      bodyLarge: "1.125rem", // 18px
      body: "1rem", // 16px
      small: "0.875rem", // 14px
      caption: "0.75rem", // 12px
    },
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },
  },
  radii: {
    sm: "0.5rem", // 8px
    md: "0.75rem", // 12px
    lg: "1rem", // 16px
    xl: "1.25rem", // 20px
    "2xl": "1.5rem", // 24px
    "3xl": "2rem", // 32px
    pill: "9999px",
    base: 20,
    card: 20,
  },
  shadows: {
    sm: "0 2px 8px -2px hsla(258, 90%, 30%, 0.08)",
    soft: "0 8px 24px -8px hsla(258, 90%, 30%, 0.12)",
    md: "0 8px 24px -8px hsla(258, 90%, 30%, 0.12)",
    lg: "0 16px 48px -12px hsla(258, 90%, 30%, 0.16)",
    glow: "0 0 60px -12px hsla(258, 90%, 58%, 0.4)",
    glass: "0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05)",
  },
  animations: {
    easing: {
      default: "cubic-bezier(0.4, 0, 0.2, 1)",
      smooth: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
    },
    duration: {
      fast: "0.2s",
      normal: "0.3s",
      slow: "0.5s",
    },
  },
  layout: {
    sidebarWidth: 280,
    headerHeight: 64,
    pagePaddingX: 32,
    landing: {
      sectionPaddingY: { desktop: 96, mobile: 48 },
      containerMaxWidth: 1400,
      containerPaddingX: { desktop: 32, mobile: 16 },
    },
  },
  breakpoints: {
    sm: 640,
    md: 768,
    lg: 1024,
    xl: 1280,
    "2xl": 1536,
  },
};

